import { capitaliseSentence } from "./services/capitaliseSentence";
import fuzzy from "fuzzy";
import { Search } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { ui } from "@patient-watch/ui";
import type { OptionT } from "./Select";

interface Props {
    label?: string;
    name: string;
    options: OptionT[];
}

function optionLabelToString(label: ReactNode): string {
    if (label === null || label === undefined) return "";
    if (typeof label === "string") return label;
    if (typeof label === "number" || typeof label === "boolean") return String(label);
    return "";
}

const CheckboxList = ({ label, name, options }: Props) => {
    const { control, setValue, watch } = useFormContext();
    const [search, setSearch] = useState<string>("");
    const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
    const [isIndeterminate, setIsIndeterminate] = useState<boolean>(false);

    const safeOptions = options ?? [];
    const selectedValues = watch(name);

    useEffect(() => {
        const selectedCount = selectedValues?.length || 0;
        setIsAllSelected(selectedCount === safeOptions.length);
        setIsIndeterminate(selectedCount > 0 && selectedCount < safeOptions.length);
    }, [selectedValues, safeOptions.length]);

    const handleSelectAll = (checked: boolean | "indeterminate") => {
        if (checked === "indeterminate") return;
        setValue(name, checked ? safeOptions.map((option) => option.value) : []);
    };

    const showSelectAll = safeOptions.length > 1;

    return (
        <>
            {label && <ui.Label>{label}</ui.Label>}
            {showSelectAll && (
                <>
                    <div className="flex items-center px-2 border-slate-300 border-b focus-within:outline-none focus-within:ring-0 text-slate-600">
                        <Search className="ml-2 shrink-0" />
                        <input
                            placeholder="Filter the options"
                            className="flex-1 bg-transparent p-2 border-0 outline-none focus-visible:outline-none focus:outline-none ring-0 focus-visible:ring-0 focus:ring-0 focus-visible:ring-offset-0 focus:ring-offset-0 text-sm"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    {!search && (
                        <div className="flex items-center px-4 py-2 border-b">
                            <ui.Checkbox
                                checked={isAllSelected || isIndeterminate}
                                onCheckedChange={handleSelectAll}
                                id="select-all"
                                className={`mr-2 ${isIndeterminate ? "opacity-50" : ""}`}
                            />
                            <ui.Label htmlFor="select-all" className="">
                                {isAllSelected ? "Deselect All" : isIndeterminate ? "Some selected" : "Select All"}
                            </ui.Label>
                        </div>
                    )}
                </>
            )}
            <Controller
                name={name}
                control={control}
                render={({ field: { value, onChange } }) => (
                    <ul className="flex flex-col px-2 py-1">
                        {fuzzy
                            .filter(search, safeOptions, {
                                pre: "<",
                                post: ">",
                                extract: (el) => optionLabelToString(el.label),
                            })
                            .sort((a, b) =>
                                optionLabelToString(a.original.label).localeCompare(
                                    optionLabelToString(b.original.label)
                                )
                            )
                            .map((o) => o.original)
                            .map((option: OptionT) => {
                                const optionId = `${name}-${option.value}`;
                                const checked = Array.isArray(value) && value.includes(option.value);
                                return (
                                    <li key={option.value}>
                                        <div className="flex items-center gap-2 p-2 rounded-md w-full">
                                            <ui.Checkbox
                                                id={optionId}
                                                checked={checked}
                                                onCheckedChange={(nextChecked: boolean | "indeterminate") => {
                                                    if (nextChecked === "indeterminate") return;
                                                    const current = Array.isArray(value) ? value : [];
                                                    const next = nextChecked
                                                        ? [...new Set([...current, option.value])]
                                                        : current.filter((v) => v !== option.value);
                                                    onChange(next);
                                                }}
                                            />
                                            <label
                                                htmlFor={optionId}
                                                className="font-normal capitalize text-sm leading-snug cursor-pointer"
                                            >
                                                {capitaliseSentence(optionLabelToString(option.label), "-")}
                                            </label>
                                        </div>
                                    </li>
                                );
                            })}
                    </ul>
                )}
            />
            <ui.FormMessage />
        </>
    );
};

export default CheckboxList;
